import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { FieldProps } from '@rjsf/utils';
import { Box, Button, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import { CategoryModal } from './CategoryModal';
import type { CategoryItem } from './types';
export type { CategoryItem };

// ─── Row components ────────────────────────────────────────────────────────────

interface RowProps {
  item: CategoryItem;
  onDelete: () => void;
  handle?: ReactNode;
  dragStyle?: React.CSSProperties;
  dragRef?: (el: HTMLDivElement | null) => void;
}

function CategoryRow({ item, onDelete, handle, dragStyle, dragRef }: RowProps): ReactElement {
  return (
    <div
      ref={dragRef}
      style={dragStyle}
      className="vtwo:flex vtwo:items-center vtwo:gap-2 vtwo:px-4 vtwo:py-3 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white"
    >
      {handle}
      <span className="vtwo:flex-1 vtwo:text-sm vtwo:text-gray-700 vtwo:truncate vtwo:min-w-0">
        {item.title}
      </span>
      {/* description and imageUrl are stored in data but not shown in the row */}
      <input type="hidden" value={item.description ?? ''} readOnly />
      <input type="hidden" value={item.imageUrl ?? ''} readOnly />
      <IconButton size="small" onClick={onDelete} aria-label="Delete category">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );
}

interface SortableRowProps {
  id: string;
  item: CategoryItem;
  onDelete: () => void;
}

function SortableCategoryRow({ id, item, onDelete }: SortableRowProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <CategoryRow
      dragRef={setNodeRef}
      dragStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
      item={item}
      onDelete={onDelete}
      handle={
        <Box
          {...attributes}
          {...listeners}
          className="vtwo:flex vtwo:cursor-grab vtwo:shrink-0 vtwo:touch-none"
        >
          <DragIndicatorIcon className="vtwo:text-gray-400 vtwo:text-[20px]" />
        </Box>
      }
    />
  );
}

// ─── Field ─────────────────────────────────────────────────────────────────────

export function CategoryListManagerField(props: FieldProps<CategoryItem[]>): ReactElement {
  const { formData, onChange, fieldPathId } = props;
  const items = formData ?? [];
  const [modalOpen, setModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const canReorder = items.length > 1;

  function handleAdd(item: CategoryItem) {
    onChange([...items, item], fieldPathId.path);
  }

  function handleDelete(index: number) {
    onChange(
      items.filter((_, i) => i !== index),
      fieldPathId.path
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.title === String(active.id));
    const newIndex = items.findIndex((it) => it.title === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(items, oldIndex, newIndex), fieldPathId.path);
  }

  let rows: ReactNode;
  if (canReorder) {
    rows = (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={items.map((it) => it.title)} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableCategoryRow
              key={item.title}
              id={item.title}
              item={item}
              onDelete={() => handleDelete(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  } else {
    rows = items.map((item, index) => (
      <CategoryRow key={item.title} item={item} onDelete={() => handleDelete(index)} />
    ));
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      {rows}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        fullWidth
        onClick={() => setModalOpen(true)}
        className="vtwo:normal-case vtwo:text-gray-600 vtwo:border-plus-border vtwo:py-2 vtwo:rounded-lg"
      >
        Add category
      </Button>

      <CategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={handleAdd}
        selectedTitles={items.map((it) => it.title)}
      />
    </Box>
  );
}
